import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { removeNotification } from '../../actions/notification';
import { toast, useSonner } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useMediaQuery } from '@/components/ui/hooks/use-media-query';
import { useFullScreenCallShown } from '@/components/voice/fullScreenCall';
import { isMobileViewport, useIsMobile } from '@/hooks/use-mobile';
import { toastPosition } from './toastPosition';

// Phones: one stack at the top centre, below the status bar and the
// navigation bar, so a toast never hides the back button or the tab bar.
const PHONE_TOAST_OFFSET = {
	top: 'calc(var(--safe-top) + var(--call-bar-height, 0px) + 3.5rem)',
	left: 'calc(var(--safe-left) + 0.75rem)',
	right: 'calc(var(--safe-right) + 0.75rem)',
};

// Over the full-screen call screen in portrait, a little lower, so the
// toast's close button and its 44px touch area stay clear of Minimize.
const PHONE_CALL_SCREEN_TOAST_OFFSET = {
	...PHONE_TOAST_OFFSET,
	top: 'calc(var(--safe-top) + 4.25rem)',
};

// Elsewhere sonner's 24px, or more where a landscape phone's notch, status
// bar or in-call bar needs it. The insets are 0 on desktop, so this stays
// 24px.
const TOAST_OFFSET = {
	top: 'max(24px, calc(var(--safe-top) + var(--call-bar-height, 0px) + 12px))',
	right: 'max(24px, calc(var(--safe-right) + 12px))',
	bottom: 'max(24px, calc(var(--safe-bottom) + 12px))',
	left: 'max(24px, calc(var(--safe-left) + 12px))',
};

// A phone in landscape lays the full-screen call screen out in two columns:
// who the call is with on the left, and the controls or the keypad on the
// right, from just under its top row. So while it shows, every toast (top
// or bottom, left, centre or right) sits at the top of the caller column
// like an iOS banner: beside the Minimize button, with room for the toast's
// close button and its 44px touch area, and no wider than the column.
// PhoneCallScreen publishes both edges, as `--call-screen-minimize-width`
// and `--call-screen-caller-width`.
const CALL_SCREEN_TOAST_LEFT =
	'max(calc(var(--safe-left) + 0.75rem), calc(var(--call-screen-minimize-width, 0px) + 1.25rem))';
const CALL_SCREEN_TOAST_OFFSET = {
	top: 'calc(var(--safe-top) + 0.5rem)',
	left: CALL_SCREEN_TOAST_LEFT,
};
const CALL_SCREEN_TOASTER_STYLE = {
	'--width': `min(356px, max(10rem, calc(var(--call-screen-caller-width, 100vw) - ${CALL_SCREEN_TOAST_LEFT} - 0.75rem)))`,
};
// Sonner puts each stack in the corner its toasts asked for (and index.css
// lifts bottom stacks above a phone's tab bar), so this pins every stack to
// the offsets above. A bottom stack's toasts hang down from the top too,
// like the others.
const CALL_SCREEN_TOASTER_CSS = `
[data-sonner-toaster][data-x-position][data-y-position] {
	top: var(--offset-top) !important;
	right: auto !important;
	bottom: auto !important;
	left: var(--offset-left) !important;
	width: var(--width) !important;
	transform: none !important;
}
[data-sonner-toaster][data-x-position][data-y-position] > [data-sonner-toast] {
	width: var(--width) !important;
}
[data-sonner-toaster][data-x-position][data-y-position] > [data-sonner-toast][data-y-position='bottom'] {
	top: 0 !important;
	bottom: auto !important;
	--lift: 1 !important;
}`;

// With every stack in one spot, sonner draws the Toaster's own stack first
// and the others over it, so a new toast there could land under an older
// one. Raise whichever stack holds the newest toast.
const newestStackCss = (position) => {
	const [y, x] = position.split('-');
	return `[data-sonner-toaster][data-y-position='${y}'][data-x-position='${x}'] { z-index: 1000000000 !important; }`;
};

function Notifications() {
	const dispatch = useDispatch();
	const notification = useSelector((state) => state.notification);
	const isMobile = useIsMobile();
	// The call screen's two-column layout follows the orientation.
	const isLandscape = useMediaQuery('(orientation: landscape)');
	const isCallScreenShown = useFullScreenCallShown();
	const isBesideCallScreen = isLandscape && isCallScreenShown;
	const { toasts } = useSonner();
	const position = isMobile ? 'top-center' : 'top-right';

	useEffect(() => {
		if (notification) {
			const toastOptions = {
				id: 'chekkit-notification',
				position: toastPosition(notification.position || 'top-right'),
				title: notification.title || 'Notification',
				description: notification.message,
				duration: 5000,
				onDismiss: () => dispatch(removeNotification()),
				// Phones dismiss with the close button or a swipe; a second
				// Close button there only crowds the toast.
				...(!isMobileViewport() && {
					cancel: {
						label: 'Close',
						onClick: () => dispatch(removeNotification()),
					},
				}),
			};

			if (notification.severity === 'error') {
				toast.error('Error', toastOptions);
			} else if (
				notification.title === 'Success' ||
				notification.severity === 'success'
			) {
				toast.success('Success', toastOptions);
			} else {
				toast(
					notification.title ? notification.title : '',
					toastOptions
				);
			}
		}
	}, [notification]);

	const offset = isBesideCallScreen
		? CALL_SCREEN_TOAST_OFFSET
		: !isMobile
			? TOAST_OFFSET
			: isCallScreenShown
				? PHONE_CALL_SCREEN_TOAST_OFFSET
				: PHONE_TOAST_OFFSET;

	// One tree shape in every case: the Toaster keeps its toasts in its own
	// state, so remounting it would drop the ones on screen.
	return (
		<>
			<Toaster
				closeButton
				richColors
				position={position}
				offset={offset}
				mobileOffset={isMobile || isBesideCallScreen ? offset : undefined}
				style={isBesideCallScreen ? CALL_SCREEN_TOASTER_STYLE : undefined}
			/>
			{isBesideCallScreen && (
				<style>
					{CALL_SCREEN_TOASTER_CSS}
					{newestStackCss(toasts[0]?.position || position)}
				</style>
			)}
		</>
	);
}

export default Notifications;
